<template lang='pug'>
.select-or-new
	.short-text-input-wrapper(
		v-if='creatingNew'
	)
		short-text-input(
			v-model='newValue'

			:last-tab-index='lastTabIndex'
			:errors='getError( "textInput" )'
			:focusFromParent='focus'

			:options='options.textInput'
		)
		.icon-add(
			v-if='options.dropdown.multiselect'
			@click='addItemToList'
		)
		.icon-close(
			@click='closeInput'
		)
	custom-dropdown(
		v-else

		:last-tab-index='lastTabIndex'
		:options='dropdownOptions'
		:errors='getError( "dropdown" )'

		@options='displayedOptions = $event'

		v-model='dropdownValue'
	)
</template>

<script>
import CustomDropdown from '@/components/form-inputs/CustomDropdown';
import ShortTextInput from '@/components/form-inputs/ShortTextInput';
import { clone } from '@/lib/utils';

export default {
	name : 'select-or-new',

	props : {

		options : {
			type : Object
		},

		value : {
			type : [Object, Array],
		},

		errors : {
			type    : [String, Boolean, Object],
			default : '',
		},

		formValue : {
			type : Object,
		},

		field : {
			type : Object,
		},

		form : {
			type : Object,
		},

		lastTabIndex : {
			type : Number,
		},

	},

	data : () => ( {
		newValue      : null,
		dropdownValue : null,

		addedOptions     : [],
		displayedOptions : [],

		creatingNew : false,

		focus : false,
	} ),

	computed : {

		localValue() {
			const { dropdownProp, textInputProp } = this.options;
			let localValue                        = {};

			if ( this.options.dropdown.multiselect ) {
				if ( this.dropdownValue === null ) {
					return [];
				}

				localValue = clone( this.dropdownValue );

				if ( this.creatingNew ) {
					const newItem          = {};
					newItem[textInputProp] = this.newValue;

					localValue.push( newItem );
				}

				else {
					localValue = localValue.filter( a => a[dropdownProp] !== '_create_new' );
				}

				return localValue;
			}

			if ( this.creatingNew ) {
				localValue[textInputProp] = this.newValue || null;
				localValue[dropdownProp]  = null;

				return localValue;
			}

			localValue[dropdownProp]  = this.dropdownValue || null;
			localValue[textInputProp] = null;

			return localValue;
		},

		dropdownOptions() {
			const { dropdown } = this.options;
			const { options }  = dropdown;

			const dropdownOptions = {
				...dropdown,
			};

			// this function can return either an array
			// or a promise because CustomDropdown will
			// handle both cases
			dropdownOptions.options = () => {
				const createNewValue = {
					text : this.options.createNewLabel || 'Create New',
				};

				const valProp           = this.options.dropdown.multiselect ? this.options.dropdownProp : 'value';
				createNewValue[valProp] = '_create_new';

				if ( Array.isArray( options ) ) {
					return [...options, ...this.addedOptions, createNewValue];
				}

				return Promise.resolve( options() ) // eslint-disable-line
					.then( selectOptions => ( [...this.addedOptions, createNewValue] ).concat( selectOptions ) );
			};

			if ( dropdown.multiselect ) {
				dropdownOptions.keyProp = this.options.dropdownProp;
			}

			return dropdownOptions;
		}

	},

	created() {
		const { dropdownProp, textInputProp, dropdown } = this.options;

		if ( dropdown.multiselect ) {
			this.newValue      = '';
			this.dropdownValue = this.value;
		}

		else {
			this.dropdownValue = this.value[dropdownProp];
			this.newValue      = this.value[textInputProp];
		}


		this.$emit( 'input', this.localValue );
	},

	methods : {

		getError( component ) {
			if ( typeof this.errors !== 'object' ) {
				return this.errors;
			}

			const prop = this.options[`${component}Prop`];
			return this.errors[prop] || '';
		},

		addItemToList() {
			const valueProp = this.options.dropdownProp;
			const values    = this.displayedOptions
				.filter( a => a[valueProp] !== '_create_new' )
				.map( a => parseInt( a[valueProp], 10 ) );

			const max     = Math.max( ...values );
			const nextKey = max + 1;

			const added = {
				text : this.newValue,
				new  : true,
			};

			added[valueProp] = nextKey;

			this.addedOptions.push( added );

			this.dropdownValue = this.dropdownValue.filter( a => a[valueProp] !== '_create_new' )
				.concat( [added] );

			this.creatingNew = false;
			this.newValue    = '';
		},

		closeInput() {
			if ( this.options.dropdown.multiselect ) {
				this.dropdownValue = this.dropdownValue.filter( a => a[this.options.dropdownProp] !== '_create_new' );
				this.newValue      = '';
				this.creatingNew   = false;

				return;
			}

			this.newValue    = '';
			this.creatingNew = false;
		}

	},

	watch : {

		localValue : {
			deep : true,
			handler( value ) {
				this.$emit( 'input', value );
			}
		},

		dropdownValue( value ) {

			this.creatingNew = ( () => {
				if ( this.options.dropdown.multiselect ) {
					return value.findIndex( a => a[this.options.dropdownProp] === '_create_new' ) !== -1;
				}

				return ( value === '_create_new' );
			} )();

			if ( this.creatingNew ) {
				this.focus = true;
				return;
			}

			this.focus = false;

		}

	},

	components : {
		ShortTextInput,
		CustomDropdown,
	}
};
</script>

<style lang='scss'>
.select-or-new {
	position: relative;

	.short-text-input-wrapper .icon-close {
		position: absolute;
		right: 15px;
		top: 50%;
		transform: translateY(-50%);

		&::before,
		&::after {
			height: 15px;
		}
	}

	.short-text {
		padding-right: 60px !important;
	}

	.dropdown {
		width: 100%;
	}

	.icon-add {
		height: 26px;
		width: 26px;
		border: 2px solid $color-primary;
		border-radius: 50%;
		position: absolute;
		right: 45px;
		top: 50%;
		transform: translateY(-50%);
		cursor: pointer;

		&::before,
		&::after {
			content: ' ';
			height: 12px;
			width: 2px;
			border-radius: 2px;
			position: absolute;
			top: 50%;
			left: 50%;
			background: $color-primary;
		}

		&::before {
			transform: translate(-50%,-50%);
		}

		&::after {
			transform: translate(-50%,-50%) rotate(90deg);
		}
	}
}
</style>
